import {DesignerMode} from '@packages/utilities/environment';
import {getDesignerSessionId} from '@packages/systems/multiplayer/session';

const CSRF_HEADER_NAME = 'X-XSRF-Token';

function getCSRFToken(): string | null {
  const metaEl = document.querySelector('meta[name=_csrf]');

  if (!metaEl) {
    return null;
  }

  const token = metaEl.getAttribute('content');

  if (!token) {
    return null;
  }

  return token;
}

function getHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init && init.headers);

  // Include csrfToken if embedded in page
  if (!headers.get(CSRF_HEADER_NAME)) {
    const csrfToken = getCSRFToken();

    if (csrfToken) {
      headers.set(CSRF_HEADER_NAME, csrfToken);
    }
  }

  // Default 'Accept' to 'application/json'
  if (!headers.get('Accept')) {
    headers.set('Accept', 'application/json');
  }

  if (!headers.get('X-Session-Id')) {
    const sessionId = maybeGetDesignerSessionId();
    if (sessionId) {
      headers.set('X-Session-Id', sessionId);
    }
  }

  // Default 'Content-Type' to 'application/json' when a body is included
  if (
    init &&
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.get('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  return headers;
}

function maybeGetDesignerSessionId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return getDesignerSessionId();
  } catch {
    return null;
  }
}

interface CancelablePromise<T> extends Promise<T> {
  cancel: () => void;
}

export default function webflowFetch(
  pathRelativeToAPIHost: string,
  init?: RequestInit,
  baseUrl?: string | URL
): CancelablePromise<Response> {
  const fullPathUrl = new URL(
    pathRelativeToAPIHost,
    baseUrl || location.origin
  );
  const headers = getHeaders(init);

  const controller = new AbortController();

  const promise = fetch(fullPathUrl, {
    credentials:
      DesignerMode.PREVIEW || DesignerMode.ANONYMOUS_COMMENT
        ? 'include'
        : 'same-origin',
    mode: 'cors',
    signal: controller.signal,
    ...init,
    headers,
  });

  // Support cancel for react-query
  (promise as CancelablePromise<Response>).cancel = () => controller.abort();

  return promise as CancelablePromise<Response>;
}
